import { captureException } from '@sentry/react';
import AssignableNotification from 'components/AssignableNotification';
import AssignedNotification from 'components/AssignedNotification';
import AssignmentNotification from 'components/AssignmentNotification';
import AssignmentPreview from 'components/AssignmentPreview';
import useBook from 'components/hooks/books/useBook';
import useStateManager from 'components/hooks/useStateManager';
import PastAssignment from 'components/PastAssignment';
import Assignment from 'features/Assignment';
import { find, get, filter } from 'lodash';
import React, { useMemo, memo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useExtraResourceEbook } from 'store/extraResource/selectors';
import { useStory } from 'store/story/selectors';
import { updateUI } from 'store/UI/actions';
import { useUI } from 'store/UI/selectors';
import { useWorkSheetSelector } from 'store/worksheet/selectors';
import styled from 'styled-components';
import NoActivity from 'utils/assets/images/noActivity.svg';
import { BACK_TO_BOOK } from 'utils/config/reference';
import { COLOR } from 'utils/constants/color';
import {
  generateUrlForNonEbook,
  getStoryUrl,
  modifyAssignmentForStudent,
  sortByCustomType,
  updateUrlPathWithActivityOrQuiz,
} from 'utils/lib/ebookHelper';
import { isPastDue } from 'utils/methods/math';
import useGetExtensionAssignments from './getExtensionAssignment';
import { getExtensionBookDataById, getExtensionPageDataByPageId } from './helper';
import GraphicAssignmentPreview from 'components/GraphicOrganizerAssignmentPreview';
import useGetEngageAssignment from 'components/hooks/assignment/useGetEngageAssignment';
import useNotification from 'components/hooks/notification/useNotification';
import SentryWrap from 'components/Sentry';
import WorkSheetModal from 'modules/workSheet';
import { useClassGameActivity } from 'store/one_game/selectors';

const AssignmentsComponent = ({
  isDrawerOpen = false,
  toggleDrawer = () => { },
  closeSideDrawer = () => {},
  //for section style
  sectionStyle = {},
  // for ebook
  bookId = '',
  pageId = '',
  id = '',
  extensionActivityId,
  student_render_assignments,
  from = '',
  isAssessmentBook = false,
  renderAssignments = '',
  currentBook = {},

  // for assignment drawer
  timmer = null,
  assignmentAllRefs = null,
  timeInt = null,
  setSubmitRef = () => { },
  onFinish = () => { },

  newMaskId = '',
  pageLearningModuleCount = {},
  isStudentTakingAssignment = false,
}) => {
  const dispatch = useDispatch();
  const { extensionActivity = {} } = useUI();
  const location = useLocation();
  const navigate = useNavigate();
  const story = useStory();
  const extraResource = useExtraResourceEbook();
  const extensionAssignment = useGetExtensionAssignments({ bookPageId: pageId });
  const listWorkSheet = useWorkSheetSelector();
  const [combinedBookIds, combinedPageIds] = extensionAssignment?.configBookIdPageId || {};
  const bookState = useBook();
  const { class_game_activity } = useClassGameActivity();
  const tipsTalented = filter(bookState?.tipsBook, (x) => x.texType === 'talented');
  const tipsSpecial = filter(bookState?.tipsBook, (x) => x.texType === 'special');
  const bookData = getExtensionBookDataById(bookState, bookId, isAssessmentBook, combinedBookIds);
  const [openGraphicAssignmentPrivew, setOpenGraphicAssignmentPrivew] = useState(false);
  const [graphicAssignment, setGraphicAssignment] = useState({});
  const [graphicAssignmentData, setGraphicAssignmentData] = useState({});
  const { openEngageModalTeacher = {} } = useUI();
  const { assignableEnageActivity, assignedEngageActivity } = useGetEngageAssignment({
    bookId,
    pageId: openEngageModalTeacher?.engagePageId,
  });
  const isAdmin = bookState?.currentUser?.role === 'admin';
  const isStudent = bookState?.currentUser?.role === 'student';
  const allEngageData = useMemo(() => {
    return currentBook?.engage || [];
  }, [currentBook?.engage]);
  const { readSingleNotification } = useNotification();
  const [workSheetData,setWorkSheetData] = useState([]);
  const assignmentDrawerRef = useRef(null);
  const [pendingScrollToAssignmentDrawer, setPendingScrollToAssignmentDrawer] = useState(false);

  const dataList = useMemo(() => {
    return listWorkSheet?.filter((item) => parseInt(item?.page_id) === parseInt(pageId))
  }, [listWorkSheet, pageId]);

  const pageData = getExtensionPageDataByPageId(
    bookState,
    bookData,
    pageId,
    combinedPageIds,
    extensionAssignment?.configExtraResource,
    bookId,
  );

  const [states, updateState] = useStateManager({
    diffSection: false,
    studentsName: [],
    studentsError: false,
    preview: false,
    assignmentData: {},
  });

  const filterExtraResource = useMemo(() => {
    return extraResource?.filter((item) => item?.pageId == pageId) || [];
  }, [pageId, extraResource]);

  const activeQuiz = pageData.activeQuiz;
  const activeActivity = pageData.activeActivity;

  const availableActivity = modifyAssignmentForStudent(
    pageData.availableActivity,
    bookState.isStudent,
    false,
    bookState.currentUser,
    bookState.servertime,
    bookState.timezone,
    bookState.currentClass,
    bookState.getHiddenBooks,
  );

  const modAssignableActivity = pageData.assignableActivity;
  const assignableActivity = useMemo(() => {
    const data = sortByCustomType(modAssignableActivity);
    const xyz = data?.filter((x) => x?.type?.toLowerCase() !== 'custom');

    const sortedActivities = [...xyz].sort((a, b) => {
      const seqA = parseInt(a.sequence_order) || 0;
      const seqB = parseInt(b.sequence_order) || 0;
      return seqA - seqB;
    });

    const finalResult = [
      ...sortedActivities,
      ...data?.filter((x) => x?.type?.toLowerCase() === 'custom'),
    ];
    return finalResult;
  }, [modAssignableActivity]);

  const worksheetAssignments = useMemo(() => {
    const currentClassId = bookState?.currentClass?.id;
    const userRole = bookState?.currentUser?.role;
    const worksheetsOnPage = Array.isArray(dataList) ? dataList : [];
    const activities = Array.isArray(class_game_activity) ? class_game_activity : [];
    if (!currentClassId || worksheetsOnPage.length === 0) {
      return { assigned: [], assignable: [] };
    }

    const assigned = [];
    const assignable = [];

    worksheetsOnPage.forEach((worksheet) => {
      if (!worksheet?.id) return;

      const assignedActivityForClass = activities.find(
        (a) =>
          a?.type === 'worksheet' &&
          String(a?.game_id) === String(worksheet.id) &&
          String(a?.classId) === String(currentClassId),
      );

      const displayName =
        assignedActivityForClass?.displayName || worksheet?.display_name || 'Grammar Drill';

      if (assignedActivityForClass?.id) {
        assigned.push({
          id: assignedActivityForClass.id,
          displayName,
          scheduleAt: assignedActivityForClass?.scheduleAt,
          dueDate: assignedActivityForClass?.dueDate,
          lateDate: assignedActivityForClass?.lateDate,
          classes: assignedActivityForClass?.classes || [],
          active: true,
          data: {
            id: worksheet.id,
            typeId: 'worksheet',
            displayName,
            chapter: bookId,
            page: pageId,
            share_by: worksheet?.user_id,
            activity_type: 'worksheet',
            is_ai: false,
          },
        });
        return;
      }

      if (userRole === 'student') {
        return;
      }

      assignable.push({
        id: worksheet.id,
        displayName,
        typeId: 'worksheet',
        chapter: bookId,
        page: pageId,
        share_by: worksheet?.user_id,
        share_list: worksheet?.share_list || [],
        createdBy: worksheet?.user_id,
        type: worksheet?.type || 'custom',
        activity_type: 'worksheet',
        is_ai: false,
      });
    });
    return { assigned, assignable };
  }, [
    bookState?.currentClass?.id,
    bookState?.currentUser?.role,
    class_game_activity,
    dataList,
    newMaskId,
    bookId,
    pageId,
  ]);

  const availableQuiz = modifyAssignmentForStudent(
    pageData.availableQuiz,
    bookState.isStudent,
    true,
    bookState.currentUser,
    bookState.servertime,
    bookState.timezone,
    bookState.currentClass,
    bookState.getHiddenBooks,
  );

  const modAssignableQuiz = pageData.assignableQuiz;
  const assignableQuiz = useMemo(() => {
    const data = sortByCustomType(modAssignableQuiz);
    const xyz = data?.filter((x) => x?.type?.toLowerCase() !== 'custom');

    const sortedActivities = [...xyz].sort((a, b) => {
      const seqA = parseInt(a.sequence_order) || 0;
      const seqB = parseInt(b.sequence_order) || 0;
      return seqA - seqB;
    });

    const finalResult = [
      ...sortedActivities,
      ...data?.filter((x) => x?.type?.toLowerCase() === 'custom'),
    ];
    return finalResult;
  }, [modAssignableQuiz]);

  const AssignableActivityAndQuiz = useMemo(() => {
    switch (renderAssignments) {
      case 'admin':
        return [...assignableActivity, ...assignableQuiz]?.filter(
          (x) => x?.type?.toLowerCase() !== 'custom',
        );
      default:
        if (openEngageModalTeacher?.open) {
          const filterEnagageAssignment = assignableEnageActivity?.filter(
            (x) => x?.config_assignment?.isFrom === 'engagement',
          );
          return filterEnagageAssignment;
        }
        return [...assignableActivity, ...assignableQuiz]?.filter(
          (x) => x?.config_assignment?.isFrom !== 'engagement',
        );
    }
  }, [assignableActivity, assignableQuiz, openEngageModalTeacher, assignableEnageActivity]);

  const AssignedActivityAndQuiz = useMemo(() => {
    const worksheets = Array.isArray(worksheetAssignments.assigned)
        ? worksheetAssignments.assigned
        : [];

    const activities = Array.isArray(availableActivity)
        ? availableActivity
        : [];

    const quizzes = Array.isArray(availableQuiz)
        ? availableQuiz
        : [];

    const mergedAssignments = [...worksheets, ...activities, ...quizzes];

    switch (renderAssignments) {
        case 'admin':
            return mergedAssignments?.filter(
                (x) =>
                    x?.data?.type?.toLowerCase() !== 'custom' ||
                    x?.data?.type === '' ||
                    x?.data?.type === null,
            );

        default:
            if (bookState?.currentUser?.role === 'student') {
                if (openEngageModalTeacher?.open) {
                    return assignedEngageActivity?.filter(
                        (x) => x?.data?.config_assignment?.isFrom === 'engagement',
                    );
                }

                return mergedAssignments;
            }

            if (openEngageModalTeacher?.open) {
                return assignedEngageActivity?.filter(
                    (x) => x?.data?.config_assignment?.isFrom === 'engagement',
                );
            }

            return mergedAssignments?.filter(
                (x) => x?.data?.config_assignment?.isFrom !== 'engagement',
            );
    }
}, [
    availableActivity,
    availableQuiz,
    openEngageModalTeacher,
    assignedEngageActivity,
    worksheetAssignments.assigned,
    bookState?.currentUser?.role,
    renderAssignments,
]);

  const AssignableActivityAndQuizMerged = useMemo(() => {
    const base = Array.isArray(AssignableActivityAndQuiz) ? AssignableActivityAndQuiz : [];
    const worksheets = Array.isArray(worksheetAssignments.assignable) ? worksheetAssignments.assignable : [];
    return [...worksheets, ...base];
  }, [AssignableActivityAndQuiz, worksheetAssignments.assignable]);

  const handleChangeMultiSelect = (event) => {
    updateState({ studentsError: false });
    updateState({ studentsName: event.target.value });
  };

  const assignmentPreivew = (assignment) => {
    try {
      const assignmentData = assignment.data ? assignment.data : assignment;
      if (assignmentData.activity_type === 'graphic_organizer') {
        setOpenGraphicAssignmentPrivew(true);
        setGraphicAssignment(assignmentData);
        setGraphicAssignmentData(assignment);
      } else {
        updateState({ preview: true });
        updateState({ assignmentData: assignment.data ? assignment.data : assignment });
      }
    } catch (error) {
      captureException(`544 Error in assignmentPreivew: ${error}`);
    }
  };

  const onPageSwipe = () => {
    updateState({ preview: false });
    updateState({ assignmentData: {} });
  };

  const handleDiffSectionToggle = (value) => {
    updateState({ diffSection: value });
  };
  const checkAssignmentActiveActivity = useMemo(() => {
    const activity_id = typeof bookState?.activity == 'string' && bookState?.activity.length > 2 && bookState?.activity !== 'null' ? bookState?.activity : null
    const quiz_id = typeof bookState?.quiz == 'string' && bookState?.quiz.length > 2 && bookState?.quiz !== 'null' ? bookState?.quiz : null
    const has_assignment_id = Boolean(activity_id || quiz_id);
    return Boolean(has_assignment_id && activeActivity)
  }, [bookState, activeActivity, activeActivity])

  const checkAssignmentActiveQuiz = useMemo(() => {
    const activity_id = typeof bookState?.activity == 'string' && bookState?.activity.length > 2 && bookState?.activity !== 'null' ? bookState?.activity : null
    const quiz_id = typeof bookState?.quiz == 'string' && bookState?.quiz.length > 2 && bookState?.quiz !== 'null' ? bookState?.quiz : null
    const has_assignment_id = Boolean(activity_id || quiz_id);
    return Boolean(has_assignment_id && activeQuiz)
  }, [bookState, activeQuiz, activeQuiz])

  const has_active_and_takable = useMemo(() => {
    const has_active_assignment = checkAssignmentActiveActivity || checkAssignmentActiveQuiz;
    const assignment = has_active_assignment ? activeActivity || activeQuiz : null;
    if (!assignment || !assignment?.id) return false;
    const submission = find(bookState?.submissions, ['assignmentId', assignment?.data.id]);
    const submissions = parseInt(get(submission, 'submissions', 0));
    const retakes = parseInt(get(assignment, 'retakes', 0));
    const is_active = get(assignment, 'active', true);
    if (!is_active) return false;
    const is_past_due = isPastDue(assignment);
    if (is_past_due) return false;
    const is_retake_available = submissions < retakes + 1;
    if (!is_retake_available) return false;
    return true;
  }, [checkAssignmentActiveQuiz, checkAssignmentActiveActivity, bookState?.submissions]);

  useEffect(() => {
    if (!pendingScrollToAssignmentDrawer || !isStudent || !has_active_and_takable) {
      return;
    }
    const timerId = window.setTimeout(() => {
      assignmentDrawerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingScrollToAssignmentDrawer(false);
    }, 320);
    return () => window.clearTimeout(timerId);
  }, [pendingScrollToAssignmentDrawer, has_active_and_takable, isStudent]);


  const generateUrl = (page, activity, quiz) => {
    const storyUrl = getStoryUrl(story, bookId, page, quiz, activity);
    const backToLabel = BACK_TO_BOOK[bookId];
    const url = generateUrlForNonEbook(id, page, activity, quiz, backToLabel, null, bookId, false);
    if (url && backToLabel) {
      return url;
    } else if (storyUrl) {
      return storyUrl;
    } else {
      updateUrlPathWithActivityOrQuiz(window.location.href, activity, quiz);
    }
  };

  const getUri = (q, a, link) => `?id=${id}&quiz=${q}&activity=${a}&link=${link}`;

  const closeAssignment = () => {
    if (from === 'ebook') {
      navigate(`/ebook?chapter=${bookId}&page=${pageId}&activity=null&quiz=null`);
    } else if (from === 'balcon') {
      navigate(generateUrl(pageId, null, null));
    } else if (from === 'podcast') {
      navigate(getUri(null, null));
    }
  };

  const onNavigateToPage = (targetPage, activityId, quizId) => {
    if (from === 'ebook') {
      const url = `/ebook?chapter=${bookId}&page=${targetPage}&activity=${activityId ?? 'null'}&quiz=${quizId ?? 'null'}`;
      navigate(url, { replace: true });
    } else if (from === 'balcon') {
      navigate(generateUrl(targetPage, activityId, quizId), { replace: true });
    } else if (from === 'podcast') {
      navigate(getUri(quizId ?? null, activityId ?? null, null), { replace: true });
    }
  };


  const navigationUrl = useCallback(async (bookId, pageId, element) => {
    readSingleNotification(bookId, pageId, bookState?.currentUser, { data: { id: element?.data?.id } });
    if (from === 'ebook') {
      navigate(`/ebook?chapter=${bookId}&page=${pageId}&activity=${element.data?.id}&quiz=null`);
    } else if (from === 'balcon') {
      navigate(generateUrl(pageId, null, element?.data?.id));
    } else if (from === 'podcast') {
      navigate(getUri(null, element?.data?.id));
    }
  }, [readSingleNotification, bookState?.currentUser, from, navigate, generateUrl, getUri]);

  const getMessageForNotActivity = () => {
    if (bookState?.currentUser?.role == 'student') {
      if (availableActivity.length == 0 && availableQuiz.length == 0 && dataList?.length == 0) {
        return <NoActivityText>{'Activity not assigned \n by teacher'}</NoActivityText>;
      } else {
        return null;
      }
    } else {
      if (
        availableActivity.length == 0 &&
        availableQuiz.length == 0 &&
        assignableActivity.length == 0 &&
        assignableQuiz.length == 0 &&
        dataList?.length == 0
      ) {
        return <NoActivityText>No activity</NoActivityText>;
      } else return null;
    }
  };

  const getCurrentExtensionActivityData = async (data, assignFrom, bookId) => {
    try {
      const is_extension_activity = assignFrom?.bookId !== bookId;
      if (!is_extension_activity || !assignFrom?.extensionId) return;
      const extraResoruceData = filterExtraResource?.find(
        (item) => item?.id === assignFrom?.extensionId,
      );
      if (!extraResoruceData) return;
      const payload = {
        open: true,
        ...extraResoruceData,
        linkFromEbook: data?.data.page,
        disableNavigation: true,
      };
      await dispatch(updateUI({ extensionActivity: payload }));
    } catch (error) {
      captureException('Error in fetch extension activity data:', error);
    }
  };

  const closeExtensionActivity = async () => {
    if (extensionActivity?.open) {
      dispatch(updateUI({ extensionActivity: { oepn: false } }));
    }
  };

  let isEmpty = false;

  if (bookState?.currentUser?.role === 'teacher' || isAdmin) {
    isEmpty =
      pageData?.pageInteractions?.length === 0 &&
      pageData?.videoSupport.length === 0 &&
      tipsTalented.length === 0 &&
      tipsSpecial.length === 0 &&
      AssignableActivityAndQuizMerged?.length === 0 &&
      AssignedActivityAndQuiz?.length === 0 &&
      dataList?.length === 0;
  } else {
    isEmpty =
      pageData?.pageInteractions?.length === 0 &&
      pageData?.videoSupport.length === 0 &&
      // tipsTalented.length === 0 &&
      // tipsSpecial.length === 0 &&
      AssignedActivityAndQuiz?.length === 0 &&
      dataList?.length === 0;
  }

  const isEmptyAssignment = isStudent
    ? AssignedActivityAndQuiz?.length === 0
    : AssignedActivityAndQuiz?.length === 0 && AssignableActivityAndQuizMerged?.length === 0;

  const EmptySection = () => {
    return (
      <>
        {isEmpty && bookState?.activity === 'null' && bookState?.quiz === 'null' && (
          <div className="empty-section-container">
            <img src={NoActivity} alt="" />
            <div className="empty-text-label">No activities to show on this page</div>
          </div>
        )}
      </>
    );
  };



  return (
    <SentryWrap>
      <Section
        ref={assignmentAllRefs}
        className={sectionStyle?.className}
        from={from}
        style={sectionStyle?.style}
        isEmptyAssignment={isEmptyAssignment}
      >
        
        {from === 'podcast' ? (
          getMessageForNotActivity()
        ) : (
          <div className="w-full flex items-center justify-center">
            <EmptySection />
          </div>
        )}
        {!isStudent && (
          <GraphicAssignmentPreview
            open={openGraphicAssignmentPrivew}
            setOpen={setOpenGraphicAssignmentPrivew}
            assignment={graphicAssignment}
            page_id={pageId}
            bookId={bookId}
          />
        )}
        {!isEmpty && <SectionHeader>Activities</SectionHeader>}
        {isStudent && !isStudentTakingAssignment && !states.preview  && (
        <div className="mt-10px">
          <WorkSheetModal setWorkSheetData={setWorkSheetData} page={pageId} bookId={bookId} numberMasked={newMaskId} pageLearningModuleCount={pageLearningModuleCount} from={from} />
        </div>
      )}
        {(bookState?.currentUser?.role === 'teacher' || isAdmin) && (
          <React.Fragment>
            {states.preview ? (
              <AssignmentPreview data={states.assignmentData} onClose={onPageSwipe} currentClass={bookState?.currentClass} from={from} />
            ) : null}
            {AssignedActivityAndQuiz?.map((element, id) => {
              if (states.preview) {
                return null;
              }
              return (
                <React.Fragment key={id}>
                  <AssignedNotification
                    assignment={element}
                    listStudents={bookState?.listStudents}
                    handleChangeMultiSelect={handleChangeMultiSelect}
                    diffSection={states.diffSection}
                    setDiffSection={handleDiffSectionToggle}
                    studentsName={states.studentsName}
                    studentsError={states.studentsError}
                    setStudentsName={(value) => {
                      updateState({ studentsName: value });
                    }}
                    worksheetData={worksheetAssignments.assigned}
                    isAssignedAllExam={from === 'ebook' && false}
                    currentClass={bookState?.currentClass}
                    onPreviewCick={() => {
                      assignmentPreivew(element);
                    }}
                    booksAllList={bookState?.booksAllList}
                    viewType={from}
                    is_extension_activity={
                      from === 'extensionActivity'
                        ? null
                        : element?.assignfrom
                          ? element?.assignfrom?.bookId !== bookId
                          : element?.data?.chapter !== bookId
                            ? true
                            : false
                    }
                    extensionActivityData={{
                      bookId: bookId,
                      pageId: pageId,
                      extensionId: extensionActivityId,
                      urlPath: location.pathname + location.search,
                    }}
                    engageData={{
                      isFrom: 'engagement',
                      engage_id: openEngageModalTeacher?.id,
                    }}
                    is_engage_activity={openEngageModalTeacher?.open && openEngageModalTeacher?.id}
                    graphicAssignment={graphicAssignment}
                  />
                </React.Fragment>
              );
            })}
            {AssignableActivityAndQuizMerged?.map((element, id) => {
              if (states.preview) {
                return null;
              }
              return (
                <React.Fragment key={id}>
                  <AssignableNotification
                    assignment={element}
                    listStudents={bookState?.listStudents}
                    handleChangeMultiSelect={handleChangeMultiSelect}
                    diffSection={states.diffSection}
                    setDiffSection={handleDiffSectionToggle}
                    studentsName={states.studentsName}
                    studentsError={states.studentsError}
                    onPreviewCick={() => {
                      assignmentPreivew(element);
                    }}
                    worksheetData={worksheetAssignments.assignable}
                    from={from}
                    extensionActivityData={{
                      bookId: bookId,
                      pageId: pageId,
                      extensionId: extensionActivityId,
                      urlPath: location.pathname + location.search,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        )}

        {bookState?.currentUser?.role === 'student' &&
          student_render_assignments &&
          !has_active_and_takable && (
            <React.Fragment>
              {AssignedActivityAndQuiz?.map((element, id) => {
                if (element?.data?.typeId === 'worksheet') return;
                const isPast = isPastDue(element, bookState?.servertime, bookState?.timezone);
                const isEngage = element?.data?.config_assignment?.isFrom === 'engagement';
                const findEngageData = isEngage
                  ? allEngageData?.find((x) => x?.id === element?.data?.config_assignment?.engage_id)
                  : null;
                if (!isPast) {
                  return (
                    <React.Fragment key={id}>
                      <AssignmentNotification
                        assignment={element}
                        onClick={() => {
                          setPendingScrollToAssignmentDrawer(true);
                          navigationUrl(bookId, pageId, element);
                        }}
                        submissions={bookState?.submissions}
                        userRol={bookState?.currentUser?.role}
                        currentUser={bookState?.currentUser}
                        currentClass={bookState?.currentClass}
                        is_extension_activity={
                          element?.assignfrom ? element?.assignfrom?.bookId !== bookId : false
                        }
                        is_engage_activity={isEngage}
                        engagetData={findEngageData?.imageData}
                      />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={id}>
                    <PastAssignment
                      assignment={element}
                      submissions={bookState?.submissions}
                      is_extension_activity={
                        element?.assignfrom ? element?.assignfrom?.bookId !== bookId : false
                      }
                      is_engage_activity={isEngage}
                      engagetData={findEngageData?.imageData}
                    />
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          )}
      </Section>

      <React.Fragment>
        <AssignmentDrawer
          ref={assignmentDrawerRef}
          type={from}
          active={has_active_and_takable}
          className="ebookInstAssignNw"
        >
          {checkAssignmentActiveActivity && has_active_and_takable ? (
            <Assignment
              chapter={bookId}
              currentSubmission={find(bookState?.submissions, [
                'assignmentId',
                activeActivity?.data.id,
              ])}
              data={activeActivity?.data}
              shuffle={activeActivity?.shuffled}
              onClose={closeAssignment}
              page={pageId}
              retakes={activeActivity.retakes}
              userRol={bookState?.currentUser?.role}
              setSubmitRef={setSubmitRef}
              dueDate={activeActivity.dueDate}
              timmer={timmer}
              onFinish={onFinish}
              timeInt={timeInt}
              redirectTo={
                from === 'balcon'
                  ? generateUrl(pageId, null, null)
                  : from === 'podcast'
                    ? getUri(null, null)
                    : undefined
              }
              toggleDrawer={toggleDrawer}
              isDrawerOpen={isDrawerOpen}
              is_extension_activity={
                activeActivity?.assignfrom ? activeActivity?.assignfrom?.bookId !== bookId : false
              }
              getCurrentExtensionActivityData={() => {
                getCurrentExtensionActivityData(activeActivity, activeActivity?.assignfrom, bookId);
              }}
              closeExtensionActivity={closeExtensionActivity}
              onNavigateToPage={onNavigateToPage}
              closeSideDrawer={closeSideDrawer}
            />
          ) : null}
          {checkAssignmentActiveQuiz && has_active_and_takable ? (
            <Assignment
              chapter={bookId}
              currentSubmission={find(bookState?.submissions, [
                'assignmentId',
                activeQuiz?.data.id,
              ])}
              data={activeQuiz?.data}
              shuffle={activeQuiz?.shuffled}
              onClose={closeAssignment}
              page={pageId}
              retakes={activeQuiz.retakes}
              userRol={bookState?.currentUser?.role}
              setSubmitRef={setSubmitRef}
              dueDate={activeQuiz.dueDate}
              timmer={timmer}
              onFinish={onFinish}
              timeInt={timeInt}
              redirectTo={
                from === 'balcon'
                  ? generateUrl(pageId, null, null)
                  : from === 'podcast'
                    ? getUri(null, null)
                    : undefined
              }
              toggleDrawer={toggleDrawer}
              isDrawerOpen={isDrawerOpen}
              is_extension_activity={
                activeQuiz?.assignfrom ? activeQuiz?.assignfrom?.bookId !== bookId : false
              }
              getCurrentExtensionActivityData={() => {
                getCurrentExtensionActivityData(activeQuiz, activeQuiz?.assignfrom, bookId);
              }}
              closeExtensionActivity={closeExtensionActivity}
              onNavigateToPage={onNavigateToPage}
              closeSideDrawer={closeSideDrawer}
            />
          ) : null}
        </AssignmentDrawer>
      </React.Fragment>
    </SentryWrap>
  );
};

export default memo(AssignmentsComponent);

const NoActivityText = styled.div`
  white-space: pre-line;
  color: ${COLOR.white};
  background-color: ${COLOR.blue};
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  padding: 5px;
  line-height: 17px;
  margin-top: 24px;
`;

const Section = styled.div`
  border-bottom: 1px dashed ${COLOR.black700};
  padding: 0px 0px 24px;
  margin-bottom: 16px;
  position: relative;

  &:first-child {
    border-bottom: none;
    margin-bottom: 0px;
    padding: 0px;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0px;
  }
`;

const AssignmentDrawer = styled.div`
  background-color: #e0e0e0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: ${(p) => (p.type === 'podcast' ? 'relative' : 'relative')};
  left: ${(p) => (p.active ? '0px' : 'calc(100% + 32px)')};
  // top: 50px;
  transition:
    left 300ms ease,
    width 300ms ease;
  width: ${(p) => (p.active ? '100%' : '0px')};
  z-index: 1;
  @media Screen and (max-width: 768px) {
    height: unset;
    padding-bottom: 50px;
  }
`;

const SectionHeader = styled.span`
  align-items: center;
  color: ${COLOR.black200};
  display: flex;
  flex-direction: row;
  font-size: 15px;
  font-weight: 700;
  justify-content: space-between;
  letter-spacing: 0.06em;
  line-height: 1.35;
  text-transform: uppercase;
  width: 100%;
  position: sticky;
  z-index: 9;
  top: -2px;
  backdrop-filter: blur(18px);
  height: 40px;
  background: #e0e0e0;
  padding-left: 16px;
  box-sizing: border-box;
  margin-inline: -12px;
  width: calc(100% + 12px);
`;
